import React, { useEffect, useMemo, useState, type CSSProperties } from "react";
import { FaRegCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  checkPurchaseStatus,
  selectTransferPayment,
  startPolling,
  stopPolling,
} from "../../features/store/transferPaymentSlice";
import { formatCurrency } from "../../utils/utils";

interface PaymentDetailsProps {
  paymentDetails?: {
    bank?: string;
    bank_name?: string;
    account_number?: string;
    account_name?: string;
    amount?: number;
    reference?: string;
    accountExpiry?: string;
  };
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({ paymentDetails }) => {
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const dispatch = useAppDispatch();
  const { paymentDetails: storedDetails, transactionStatus } = useAppSelector(
    selectTransferPayment,
  );
  const details = useMemo(
    () => paymentDetails || storedDetails,
    [paymentDetails, storedDetails],
  );

  const CountdownTimer = ({ targetTime }: { targetTime: string }) => {
    const [timeLeft, setTimeLeft] = useState("00:00");

    useEffect(() => {
      const target = new Date(targetTime).getTime();
      const interval = setInterval(() => {
        const now = Date.now();
        const diff = target - now;

        if (diff <= 0) {
          setTimeLeft("00:00");
          clearInterval(interval);
          return;
        }

        const totalSeconds = Math.floor(diff / 1000);
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
        const seconds = String(totalSeconds % 60).padStart(2, "0");

        setTimeLeft(`${minutes}:${seconds}`);
      }, 1000);

      return () => clearInterval(interval);
    }, [targetTime]);

    return <>{timeLeft}</>;
  };

  const ExpiringClock = () => {
    return (
      <div style={{ width: 70, height: 80, margin: "auto" }}>
        <svg width="100%" height="100%" viewBox="0 0 100 100">
          {/* Stopwatch button nub */}
          <rect x="40" y="0" width="20" height="13" rx="2" fill="#f57c00" />

          {/* Circle outline */}
          <circle
            cx="50"
            cy="58"
            r="45"
            stroke="#f57c00"
            strokeWidth="5"
            fill="none"
          />

          {/* Center dot */}
          <circle cx="50" cy="58" r="2" fill="#333" />

          {/* Rotating clock hand */}
          <line
            className="clock-hand"
            x1="50"
            y1="58"
            x2="50"
            y2="23"
            stroke="#333"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>

        {/* Inline or external CSS animation */}
        <style>
          {`
          .clock-hand {
            transform-origin: 50% 58%;
            animation: spin 10s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
        </style>
      </div>
    );
  };

  useEffect(() => {
    if (!details?.reference) return;
    if (
      transactionStatus &&
      ["SUCCESSFUL", "FAILED", "CANCELLED"].includes(
        transactionStatus.toUpperCase(),
      )
    ) {
      dispatch(stopPolling());
      return;
    }

    dispatch(startPolling());

    const interval = setInterval(() => {
      dispatch(checkPurchaseStatus(details.reference as string));
    }, 5000);

    return () => {
      clearInterval(interval);
      dispatch(stopPolling());
    };
  }, [dispatch, details?.reference, transactionStatus]);

  useEffect(() => {
    if (!copied) return;

    const timeout = window.setTimeout(() => {
      setCopied(false);
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [copied]);

  const handleCopyAccountNumber = async () => {
    const accountNumber = details?.account_number;

    if (!accountNumber) return;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(accountNumber);
        setCopied(true);
        toast.success("Account number copied.");
        return;
      } catch (error) {
        // Fall back to manual copy below.
      }
    }

    const textarea = document.createElement("textarea");
    textarea.value = accountNumber;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    setCopied(true);
    toast.success("Account number copied.");
  };

  if (!details) return null;

  return (
    <>
      <div className="font-satoshi dark:text-white">
        <p className="font-semibold text-sm lg:text-base ">Payment Details</p>
        <p className="pt-3 pb-3">
          Transfer the payment into the bank details below
        </p>
        <p className=""> Bank </p>
        <p className="font-semibold pb-2">
          {details?.bank_name || details?.bank || "Bank Name"}
        </p>

        <p> Account Number </p>
        <div className="flex items-center gap-2 pb-2">
          <p className="font-semibold m-0">
            {details?.account_number || "0000000000"}
          </p>
          {!!details?.account_number && (
            <button
              type="button"
              onClick={handleCopyAccountNumber}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              aria-label="Copy account number"
              title="Copy account number"
            >
              <FaRegCopy size={12} />
              <span>{copied ? "Copied" : "Copy"}</span>
            </button>
          )}
        </div>

        <p> Account Name </p>
        <p className="font-semibold pb-2">
          {details?.account_name || "Acct Name"}
        </p>

        <p> Amount </p>
        <p className="font-semibold pb-2">
          ₦{formatCurrency(details?.amount || 0)}
        </p>
      </div>

      {transactionStatus?.toUpperCase() === "SUCCESSFUL" ? (
        <div className="mt-4 p-4 rounded-md bg-green-50 text-green-700 text-center">
          Payment received! We are completing your order.
        </div>
      ) : sent ? (
        <div className=" dark:text-white">
          <p className="font-medium text-sm text-dark-800 dark:text-gray-100 md:text-base text-center">
            <b> Receiving your transfer...</b>
          </p>
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
          </div>
          <p className="font-satoshi font-medium text-sm text-dark-700  md:text-base text-center">
            You can leave this page, we will notify you once the payment is
            received
          </p>
        </div>
      ) : (
        <>
          <p className="text-red-500 text-center m-0 p-0">
            <b> This account is for this transaction only.</b>
          </p>
          <p className="font-medium text-sm  text-center m-0 p-0  dark:text-white">
            Expires in{" "}
            <b>
              {details?.accountExpiry && (
                <CountdownTimer targetTime={details?.accountExpiry} />
              )}
            </b>
          </p>
          <ExpiringClock />
          <p className="text-center p-3">
            <button
              className=" px-4 py-2 text-[10px] bp:text-sm font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2]"
              onClick={() => setSent(true)}
            >
              I've sent the money
            </button>
          </p>
        </>
      )}
    </>
  );
};

const styles: { container: CSSProperties; arrow: CSSProperties } = {
  container: {
    width: "100%",
    height: "100px",
    display: "flex",
    alignItems: "center",
  },
  arrow: {
    fontSize: "30px",
    position: "absolute",
    animation: "moveArrow 1.5s linear infinite",
  },
};
export default PaymentDetails;
